import {
  useCartContext
} from "@/context/cart"
import {
  useEventingContext
} from "@/context/eventing"
import {
  useIsMobile
} from "@/hooks/use-is-mobile"
import {
  ConfigurableProduct
} from "@/types/graphql.prod"
import {
  deriveErrorMessage
} from "@/util/deriveErrorMessage"
import {
  generateUrl
} from "@/util/imageUtils"
import {
  gql,
  useMutation
} from "@apollo/client"
import {
  useCallback, useMemo, useState
} from "react"
import {
  useIp
} from "../ip-provider"


export const CartTriggerFragment = gql`
    fragment CartTriggerFragment on Cart {
        id
        total_quantity
    }
`

export const ProductListFragment = gql`
    fragment ProductListFragment on Cart {
        id
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        items {
            uid
            # eslint-disable-next-line @graphql-eslint/require-id-when-available
            select_product_image
            product {
                uid
                name
                sku
                url_key
                thumbnail {
                    url
                }
                presale_product {
                    status
                    label
                    note
                    shipping_to
                    shipping_from
                }
                stock_status
                # eslint-disable-next-line @graphql-eslint/require-id-when-available
                ... on ConfigurableProduct {
                    variants {
                        attributes {
                            uid
                        }
                        # eslint-disable-next-line @graphql-eslint/require-id-when-available
                        product {
                            uid
                            thumbnail {
                                url
                            }
                            presale_product {
                                status
                                label
                                note
                                shipping_to
                                shipping_from
                            }
                        }
                    }
                }
            }
            prices {
                price {
                    currency
                    value
                }
                total_item_discount {
                    value
                }
            }
            quantity
            # eslint-disable-next-line @graphql-eslint/require-id-when-available
            ... on ConfigurableCartItem {
                # eslint-disable-next-line @graphql-eslint/require-id-when-available
                configurable_options {
                    configurable_product_option_uid
                    option_label
                    configurable_product_option_value_uid
                    value_label
                }
            }
        }
    }
`

export const MiniCartFragment = gql`
    fragment MiniCartFragment on Cart {
        id
        total_quantity
        prices {
            subtotal_excluding_tax {
                currency
                value
            }
            subtotal_including_tax {
                currency
                value
            }
            grand_total {
                currency
                value
                __typename
            }
        }
        ...ProductListFragment
    }
    ${ProductListFragment}
`;

export const ADD_PRODUCT_TO_CART = gql`
    mutation AddProductToCart($cartId: String!, $product: CartItemInput!) {
        addProductsToCart(cartId: $cartId, cartItems: [$product]) {
            cart {
                id
                ...CartTriggerFragment
                ...MiniCartFragment
            }
        }
    }
    ${CartTriggerFragment}
    ${MiniCartFragment}
`


const track = ({
  product, quantity 
}: { product: any; quantity: number }) => {
  try {
    if (product) {
      const currency = product?.price_range?.minimum_price?.final_price?.currency || product?.price_range?.maximum_price?.final_price?.currency
      const price = product?.price_range?.minimum_price?.final_price?.value || product?.price_range?.maximum_price?.final_price?.value

      window.dataLayer && window.dataLayer.push({
        ecommerce: null 
      })
      window.dataLayer && window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: currency,
          value: price * quantity,
          quantity: quantity,
          items: [
            {
              item_name: product.name,
              item_id: product.sku,
              price: price,
              quantity: quantity,
            },
          ],
        },
      })

      // Pinterest
      window.pintrk && window.pintrk("track", "addtocart", {
        value: price * quantity,
        order_quantity: quantity,
        currency: currency,
        line_items: [
          {
            product_name: product.name,
            product_id: product.uid,
            product_category: null,
            product_price: price,
            product_brand: "kingsyard",
          },
        ],
      })

      // fbq
      window.fbq && window.fbq("track", "AddToCart", {
        content_ids: [product.sku],
        content_name: product.name,
        content_type: "product",
        contents: [
          {
            product_name: product.name,
            product_id: product.uid,
            product_category: null,
            product_price: price,
            product_brand: "kingsyard",
          },
        ],
        currency: currency,
        value: price * quantity,
      })
    }
  } catch {}
}


export const useAddToCart = ({
  product,
  mediaGalleryEntries,
  selectedOptionsArray,
  afterSubmit
}:{
  product: ConfigurableProduct,
  mediaGalleryEntries: any,
  selectedOptionsArray: any,
  afterSubmit?:any
}) => {

  const [isAddingProduct, setIsAddingProduct] = useState(false)
  const [addToCartStatus, setAddToCartStatus] = useState(false)
  const [addToCartQty, setAddToCartQty] = useState(0)

  const {
    isMobile
  } = useIsMobile()


  const [{
    cartId 
  }] = useCartContext()

  const [addProductToCart, {
    error: errorAddingProductToCart, 
    loading: isAddProductLoading 
  }] = useMutation(ADD_PRODUCT_TO_CART)

  const [, {
    dispatch 
  }] = useEventingContext()


  const scrollToTop = useCallback(() => {
    if (isMobile) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth",
      })
    }
  }, [isMobile])

  const {
    currentIp
  } = useIp()

  const handleAddToCart = async (quantity: number) => {
    track({
      product,
      quantity
    })

    // In your add to cart handler


    let media = mediaGalleryEntries && mediaGalleryEntries[0]

    // 默认展示图片position最小的
    mediaGalleryEntries && mediaGalleryEntries.forEach((item: any, index: number) => {
      if (item.position < media.position) {
        media = item
      }
    })

    const variables: any = {
      cartId,
      product: {
        sku: product.sku,
        quantity,
        product_image: process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL + generateUrl(mediaGalleryEntries && media?.file, "image-product")(90, 132),
      },
      entered_options: [
        {
          uid: product.uid,
          value: product.name,
        },
      ],
    }

    if (selectedOptionsArray.length) {
      variables.product.selected_options = selectedOptionsArray
    }

    try {
      setIsAddingProduct(true)

      await addProductToCart({
        variables 
      })

      // const selectedOptionsLabels = selectedOptionsArray?.map((uid, i) => ({
      //   attribute: product.configurable_options[i].label,
      //   value: product.configurable_options[i].values.findLast((x) => x.uid === uid)?.label || null,
      // })) || null

      setAddToCartQty(quantity)
      scrollToTop()
      setAddToCartStatus(true)
      setIsAddingProduct(false)
      setTimeout(() => {
        setAddToCartStatus(false)
      }, 3000)

      afterSubmit && afterSubmit()

      try {
        fetch("/api/pinterest-add-to-cart", {
          method: "POST",
          body: JSON.stringify({
            product,
            productPrice: product?.price_range?.maximum_price,
            quantity,
            ip: currentIp
          })
        });
      } catch (e) {
        console.log("Error", e);
      }
    } catch {
      setIsAddingProduct(false)
    }
  }

  const derivedErrorMessage = useMemo(
    () => deriveErrorMessage([errorAddingProductToCart]),
    [errorAddingProductToCart]
  )


  return {
    handleAddToCart,
    isAddingProduct,
    addToCartStatus,
    addToCartQty,
    errorMessage: derivedErrorMessage,
  }
}
